import { Layout } from '@/components/layout/Layout';
import { motion } from 'framer-motion';
import { RotateCcw, AlertTriangle, CheckCircle2, HelpCircle } from 'lucide-react';
import { Card } from '@/components/ui/card';

export default function Refunds() {
  return (
    <Layout>
      <div className="container mx-auto px-4 py-12 max-w-4xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="flex items-center gap-4 mb-8">
            <div className="p-3 rounded-2xl bg-primary/10">
              <RotateCcw className="h-8 w-8 text-primary" />
            </div>
            <h1 className="text-4xl font-bold text-slate-900 dark:text-white">Cancellations & Refunds</h1>
          </div>

          <p className="text-muted-foreground mb-12 text-lg">
            At NexQR, we strive to provide the best service possible. If you are not satisfied with your purchase, please review our refund and cancellation policy below.
          </p>

          <div className="prose dark:prose-invert max-w-none space-y-12">
            <section className="space-y-4">
              <h2 className="text-2xl font-bold flex items-center gap-2">
                <RotateCcw className="h-6 w-6 text-primary" />
                Cancellation Policy
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                You can cancel your subscription at any time through your account settings.
              </p>
              <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                <li>Cancellation will be effective at the end of the current billing cycle.</li>
                <li>You will continue to have access to all premium features until the cycle ends.</li>
                <li>No further charges will be applied to your account after cancellation.</li>
              </ul>
            </section>

            <section className="space-y-4">
              <h2 className="text-2xl font-bold flex items-center gap-2">
                <CheckCircle2 className="h-6 w-6 text-red-500" />
                Refund Policy
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                NexQR operates on a <strong>No Refund Policy</strong>. All sales are final, and we do not offer any money-back guarantees or trials for our paid plans. 
              </p>
              <div className="grid grid-cols-1 gap-6 mt-6">
                <Card className="p-6 border-l-4 border-l-red-500 bg-red-500/5">
                  <h3 className="font-bold mb-2">Policy Details</h3>
                  <ul className="text-sm space-y-2 text-muted-foreground">
                    <li>No refunds for monthly or annual subscription payments.</li>
                    <li>No partial refunds for unused portions of a billing cycle.</li>
                    <li>Trial periods are not offered at this time.</li>
                    <li>Accounts terminated due to violation of Terms of Service are not eligible for any compensation.</li>
                  </ul>
                </Card>
              </div>
            </section>

            <section className="space-y-4 bg-muted/30 p-8 rounded-3xl">
              <h2 className="text-2xl font-bold flex items-center gap-2">
                <HelpCircle className="h-6 w-6 text-primary" />
                How to Request a Refund
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                To request a refund, please send an email to <strong>billing@nexqr.online</strong> with the following details:
              </p>
              <ul className="list-none space-y-3 text-sm text-muted-foreground">
                <li className="flex items-start gap-2">
                  <div className="h-1.5 w-1.5 rounded-full bg-primary mt-1.5 flex-shrink-0" />
                  <span>Full Name and Account Email</span>
                </li>
                <li className="flex items-start gap-2">
                  <div className="h-1.5 w-1.5 rounded-full bg-primary mt-1.5 flex-shrink-0" />
                  <span>Transaction ID / Invoice Number</span>
                </li>
                <li className="flex items-start gap-2">
                  <div className="h-1.5 w-1.5 rounded-full bg-primary mt-1.5 flex-shrink-0" />
                  <span>Reason for the refund request</span>
                </li>
              </ul>
              <p className="text-sm font-medium mt-4">
                Refunds are processed within <strong>5-7 business days</strong> and will be credited back to the original payment method used during purchase.
              </p>
            </section>

            <div className="p-6 bg-yellow-500/5 border border-yellow-500/20 rounded-2xl flex items-start gap-4">
              <AlertTriangle className="h-6 w-6 text-yellow-500 flex-shrink-0 mt-1" />
              <div>
                <h4 className="font-bold text-yellow-600 mb-1 text-sm">Note for Enterprise Customers</h4>
                <p className="text-xs text-yellow-700/80 leading-relaxed">
                  Enterprise contract cancellations and refunds are governed by the specific Service Level Agreement (SLA) signed during onboarding. Please contact your dedicated account manager for more information.
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </Layout>
  );
}
